import { useEffect, useMemo, useRef, useState } from 'react'
import type { Lesson, LessonNoteEditingPayload, LessonStatus } from '@/types'
import type { LessonCallStateEvent } from '@/webrtc/callContext'
import { getEchoClient, useEchoClient } from './echoClient'

/** Полезная нагрузка события `lesson.call.state` из канала урока. */
export type LessonCallStatePayload = LessonCallStateEvent

/**
 * Полезная нагрузка `lesson.status.changed` (см. `App\Events\LessonStatusChanged`).
 * Содержит всё нужное, чтобы обновить строку списка без запроса к API.
 */
export type LessonStatusChangedPayload = {
  lesson_id: number
  status: LessonStatus
  actual_start_at: string | null
  actual_end_at: string | null
}

export type LessonRealtimeUpdateType =
  | 'status'
  | 'note'
  | 'homework'
  | 'word'
  | 'theme'
  | 'exercise'
  | 'test'

export type { LessonNoteEditingPayload, LessonNoteEditingState } from '@/types'

export type LessonPresenceUser = { id: number; name: string }

const ECHO_DEBUG =
  import.meta.env.DEV || String(import.meta.env.VITE_ECHO_DEBUG || '') === 'true'

function echoDevLog(...args: unknown[]) {
  if (ECHO_DEBUG) {
    console.info('[Echo]', ...args)
  }
}

/** Короткий сигнал при входе другого пользователя в presence-канал урока (Web Audio API). */
function playJoinSound() {
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return
  const ctx = new AC()
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.type = 'sine'
  osc.frequency.setValueAtTime(880, ctx.currentTime)
  gain.gain.setValueAtTime(0.1, ctx.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4)
  osc.start(ctx.currentTime)
  osc.stop(ctx.currentTime + 0.4)
}

function normalizePresenceUsers(raw: unknown[]): LessonPresenceUser[] {
  return raw.map((u) => {
    const o = u as { id?: unknown; name?: unknown }
    return {
      id: Number(o.id),
      name: String(o.name ?? ''),
    }
  }).filter((u) => Number.isFinite(u.id))
}

function leaveLessonChannel(echo: ReturnType<typeof getEchoClient>, channelName: string) {
  try {
    /** socket.io connector: уходит и presence-, и private-вариант по базовому имени */
    echo.leave(channelName)
  } catch {
    // ignore
  }
}

export function useLessonRealtime(
  lessonId: number | null,
  onUpdate: (type: LessonRealtimeUpdateType) => void,
  options?: {
    onNoteEditing?: (payload: LessonNoteEditingPayload) => void
    /** Состояние звонка на уроке — начат, кто-то вошёл/вышел, завершён. */
    onCallState?: (payload: LessonCallStatePayload) => void
    /**
     * Статус урока со всеми полями события. Позволяет обновить
     * экран сразу, не дожидаясь ответа API.
     */
    onStatusChanged?: (payload: LessonStatusChangedPayload) => void
    authUserId?: number | null
  },
) {
  const [isConnected, setIsConnected] = useState(false)
  const [presentUsers, setPresentUsers] = useState<LessonPresenceUser[]>([])
  const onUpdateRef = useRef(onUpdate)
  const onNoteEditingRef = useRef(options?.onNoteEditing)
  const onCallStateRef = useRef(options?.onCallState)
  const onStatusChangedRef = useRef(options?.onStatusChanged)
  const authUserIdRef = useRef(options?.authUserId)

  onUpdateRef.current = onUpdate
  onNoteEditingRef.current = options?.onNoteEditing
  onCallStateRef.current = options?.onCallState
  onStatusChangedRef.current = options?.onStatusChanged
  authUserIdRef.current = options?.authUserId

  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  useEffect(() => {
    if (!Number.isFinite(lessonId)) {
      setIsConnected(false)
      setPresentUsers([])
      return
    }

    let didJoin = false
    const channelName = `lesson.${lessonId}`
    const presenceKey = `presence-${channelName}`

    setIsConnected(false)
    setPresentUsers([])

    const connector = echo.connector as { channels?: Record<string, unknown> }
    if (connector.channels?.[presenceKey]) {
      leaveLessonChannel(echo, channelName)
    }

    const channel = echo.join(channelName)

    type ChannelWithLifecycle = typeof channel & {
      subscribed?: (cb: () => void) => typeof channel
      error?: (cb: (err: unknown) => void) => typeof channel
    }
    const ch = channel as ChannelWithLifecycle

    if (typeof ch.subscribed === 'function') {
      ch.subscribed(() => {
        setIsConnected(true)
        echoDevLog(
          `Подписка на presence-${channelName} подтверждена (см. также POST /api/broadcasting/auth в Network)`,
        )
      })
    } else {
      setIsConnected(true)
      echoDevLog(`presence-${channelName}: колбэка subscribed() нет — подписка считается активной`)
    }

    if (typeof ch.error === 'function') {
      ch.error((err) => {
        setIsConnected(false)
        console.error('[Echo] Ошибка подписки на presence-канал', channelName, err)
      })
    }

    channel
      .here((users: unknown[]) => {
        setPresentUsers(normalizePresenceUsers(users))
      })
      .joining((user: unknown) => {
        const u = normalizePresenceUsers([user])[0]
        if (!u) return
        const selfId = authUserIdRef.current
        if (selfId != null && u.id === selfId) return
        playJoinSound()
        setPresentUsers((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, u]))
      })
      .leaving((user: unknown) => {
        const u = normalizePresenceUsers([user])[0]
        if (!u) return
        setPresentUsers((prev) => prev.filter((x) => x.id !== u.id))
      })

    channel.listen('.lesson.status.changed', (payload: LessonStatusChangedPayload) => {
      // Сначала полезная нагрузка, потом запрос: событие уже несёт
      // статус и actual_start_at, и экран не зависит от того, успел ли ответ API
      // увидеть новое состояние.
      onStatusChangedRef.current?.(payload)
      onUpdateRef.current('status')
    })
    channel.listen('.lesson.note.changed', () => onUpdateRef.current('note'))
    channel.listen('.lesson.homework.changed', () => onUpdateRef.current('homework'))
    channel.listen('.lesson.word.added', () => onUpdateRef.current('word'))
    channel.listen('.lesson.word.removed', () => onUpdateRef.current('word'))
    // Смена темы урока: событие несёт только идентификаторы, поэтому блок
    // «Программа» перезапрашивает тему сам — с учётом политики видимости.
    channel.listen('.lesson.theme.changed', () => onUpdateRef.current('theme'))
    // Задания темы: выдача классу, сдача, проверка и снятие. Блок «Программа»
    // перезапрашивает по этому сигналу только сводку попыток — сами задания
    // на сдаче не меняются.
    channel.listen('.exercise.run.launched', () => onUpdateRef.current('exercise'))
    // Сдача и проверка приходят сюда БЕЗЛИЧНЫМ сигналом: канал урока — общий
    // для класса, и балл с идентификатором ученика в нём означал бы, что
    // оценка соседа лежит в сокет-кадрах каждого. Полная нагрузка осталась в
    // приватном канале самой попытки; сводку клиент перечитывает сам, и сервер
    // отдаёт учителю класс, а ученику только его работы.
    channel.listen('.exercise.lesson.progress.changed', () => onUpdateRef.current('exercise'))
    channel.listen('.exercise.run.cancelled', () => onUpdateRef.current('exercise'))
    // Из слов урока собран тест-черновик (авто при завершении или вручную).
    channel.listen('.lesson.words.test.created', () => onUpdateRef.current('test'))
    channel.listen('.lesson.note.editing', (payload: LessonNoteEditingPayload) => {
      onNoteEditingRef.current?.(payload)
    })
    // Звонок идёт по тому же каналу урока: подписан и тот, кого ещё не позвали,
    // иначе входящий звонок было бы некому показать.
    channel.listen('.lesson.call.state', (payload: LessonCallStatePayload) => {
      onCallStateRef.current?.(payload)
    })

    didJoin = true

    return () => {
      if (didJoin) {
        leaveLessonChannel(echo, channelName)
        didJoin = false
      }
      setIsConnected(false)
      setPresentUsers([])
    }
  }, [echo, lessonId])

  return { isConnected, presentUsers }
}

/**
 * Сколько уроков со списка держать подписанными одновременно.
 *
 * У ученика в списке легко оказывается расписание на месяцы вперёд, а каждая
 * подписка — отдельный POST /broadcasting/auth. Берём ближайшие к «сейчас»:
 * урок, который вот-вот начнётся, важнее того, что через три недели.
 */
const LESSONS_LIST_MAX_CHANNELS = 12

/** Как часто пересматривается окно ближайших уроков. */
const LESSONS_LIST_WINDOW_TICK_MS = 60_000

/**
 * Урок, ради которого стоит держать канал.
 *
 * `planned` обязателен: событие старта приходит на канал урока, который ещё
 * не начат. Подписка только на `in_progress` ловила бы старт уже начатого
 * урока — то есть не ловила бы его никогда.
 */
function isLiveLesson(lesson: Lesson): boolean {
  return lesson.status === 'planned' || lesson.status === 'in_progress'
}

/** Ближайшие к «сейчас» уроки: идущий всегда впереди запланированных. */
function selectLiveLessonIds(lessons: Lesson[], nowMs: number): number[] {
  return lessons
    .map((l) => ({
      id: l.id,
      weight: l.status === 'in_progress'
        ? -1
        : Math.abs(new Date(l.planned_start_at ?? 0).getTime() - nowMs),
    }))
    .sort((a, b) => a.weight - b.weight || a.id - b.id)
    .slice(0, LESSONS_LIST_MAX_CHANNELS)
    .map((l) => l.id)
    .sort((a, b) => a - b)
}

/** Минутный тик: окно ближайших уроков должно съезжать без перезагрузки страницы. */
function useMinuteTick(enabled: boolean): number {
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    if (!enabled) return

    const timer = window.setInterval(() => setNowMs(Date.now()), LESSONS_LIST_WINDOW_TICK_MS)
    return () => window.clearInterval(timer)
  }, [enabled])

  return nowMs
}

/** Набор уроков под подписку — с учётом лимита каналов и хода времени. */
function useLiveLessonIds(lessons: Lesson[]): number[] {
  const liveLessons = useMemo(() => lessons.filter(isLiveLesson), [lessons])

  // Тик нужен, только когда уроков больше, чем каналов: иначе набор от «сейчас»
  // не зависит, и перерисовка раз в минуту была бы напрасной.
  const nowMs = useMinuteTick(liveLessons.length > LESSONS_LIST_MAX_CHANNELS)

  return useMemo(() => selectLiveLessonIds(liveLessons, nowMs), [liveLessons, nowMs])
}

/**
 * Общая часть списочных подписок: держит приватные каналы статуса
 * `lesson.{id}.status` и разводит события в колбэки. Вынесена, чтобы список
 * учителя и список ученика не разъезжались по набору событий.
 *
 * Канал статуса, а не presence-канал урока. Presence означает «человек на
 * занятии»: его авторизация ставит ученику отметку присутствия. Список уроков
 * подписывался именно на него — и ученик, всего лишь открывший `/lessons`,
 * молча получал «был на занятии», терял пометку «тема пройдена без вас» в
 * программе и горел «онлайн» в плитках участников, ни разу не зайдя в урок.
 * Presence остаётся только за страницей урока, где присутствие — правда.
 *
 * По каналу статуса едет одно событие — `lesson.status.changed`. Прежние
 * `note`/`homework`-подписки списка сюда не переносятся: они броадкастятся
 * только на presence-канал, и слушать их здесь было бы обманом — строка
 * списка всё равно не обновится.
 */
function useLessonsListChannels(
  lessonIds: number[],
  onRefresh: () => void,
  onStatusChanged?: (payload: LessonStatusChangedPayload) => void,
) {
  const onRefreshRef = useRef(onRefresh)
  const onStatusChangedRef = useRef(onStatusChanged)
  onRefreshRef.current = onRefresh
  onStatusChangedRef.current = onStatusChanged

  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()
  const lessonIdsKey = lessonIds.join(',')

  useEffect(() => {
    if (!lessonIdsKey) return

    const ids = lessonIdsKey
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isFinite(n))

    const channelNames: string[] = []

    for (const id of ids) {
      const channelName = `lesson.${id}.status`
      channelNames.push(channelName)

      echo
        .private(channelName)
        .listen('.lesson.status.changed', (payload: LessonStatusChangedPayload) => {
          // Полезная нагрузка применяется до запроса: событие несёт статус и
          // actual_start_at, поэтому строка списка обновится, даже если ответ
          // API придёт позже или вовсе не придёт.
          onStatusChangedRef.current?.(payload)
          onRefreshRef.current()
        })

      echoDevLog(`Список уроков: подписка на private-${channelName}`)
    }

    return () => {
      for (const channelName of channelNames) {
        leaveLessonChannel(echo, channelName)
      }
    }
  }, [echo, lessonIdsKey])
}

/**
 * Подписка на каналы статуса уроков **со списка** (интерфейс преподавателя на `/lessons`).
 * Страница деталей урока не монтируется — без этого хука WebSocket `subscribe` не уходит.
 * Свои уроки (teacher_id) или все в списке для админа.
 */
export function useTeachingLessonsListRealtime(
  lessons: Lesson[],
  authUserId: number | null,
  isAdmin: boolean,
  onRefresh: () => void,
  onStatusChanged?: (payload: LessonStatusChangedPayload) => void,
) {
  const myLessons = useMemo(
    () => lessons.filter((l) => isAdmin || (authUserId != null && l.teacher_id === authUserId)),
    [lessons, authUserId, isAdmin],
  )
  const lessonIds = useLiveLessonIds(myLessons)

  useLessonsListChannels(lessonIds, onRefresh, onStatusChanged)
}

/**
 * То же для списка уроков ученика (`/lessons` в роли ученика).
 *
 * Отдельный хук, а не ветка в учительском: у ученика нет ни teacher_id, ни
 * прав админа — фильтровать нечего, в списке и так только его уроки (API
 * отдаёт их по `as_student=1`). Без этой подписки ученик, ждущий урок с
 * открытым списком, не узнаёт о старте вообще: страница грузит данные один
 * раз и поллинга у неё нет.
 *
 * Канал — приватный `lesson.{id}.status`: открытый список не должен считаться
 * присутствием ученика на занятии.
 */
export function useStudentLessonsListRealtime(
  lessons: Lesson[],
  onRefresh: () => void,
  onStatusChanged?: (payload: LessonStatusChangedPayload) => void,
) {
  const lessonIds = useLiveLessonIds(lessons)

  useLessonsListChannels(lessonIds, onRefresh, onStatusChanged)
}
